<template>
  <v-container>
    <!-- Modern Header Layout -->
    <v-row class="mb-6 align-center px-4">
      <div class="d-flex align-center py-4">
        <div>
          <div class="text-h4 font-weight-black primary--text d-flex align-center">
            Áreas y Estaciones de Producción
          </div>
          <div class="caption grey--text font-weight-medium">Gestión de recursos, capacidades y horarios</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <v-btn color="primary" depressed class="px-6 shadow-sm" @click="create">
          <v-icon small class="mr-2">fas fa-plus</v-icon>
          Nueva Área
        </v-btn>
      </div>
    </v-row>

    <!-- Search Area -->
    <v-row justify="end" class="px-4 mb-4">
      <v-col cols="12" md="5">
        <v-text-field
          v-model="search"
          label="Buscar área..."
          prepend-inner-icon="fas fa-search"
          outlined
          dense
          hide-details="auto"
          clearable
          class="modern-search-field"
          :disabled="loading"
        />
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-card outlined class="modern-card elevation-0 overflow-hidden mx-4">
      <v-data-table
        :headers="headers"
        :items="machines"
        :loading="loading"
        loading-text="Sincronizando producción..."
        :search="search"
        :sort-by="['name']"
        class="modern-table"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50],
        }"
      >
        <template v-slot:item.name="{ item }">
          <div class="body-2 font-weight-black primary--text">{{ item.name }}</div>
          <div v-if="item.equipments && item.equipments.length > 0" class="d-flex gap-1 mt-1 flex-wrap">
            <v-chip
              v-for="eq in item.equipments"
              :key="eq.id"
              x-small
              label
              :color="eq.enabled ? 'blue-grey lighten-5' : 'grey lighten-4'"
              :text-color="eq.enabled ? 'blue-grey darken-3' : 'grey darken-1'"
              class="font-weight-medium"
            >
              {{ eq.name }}
            </v-chip>
          </div>
        </template>

        <template v-slot:item.capacity="{ item }">
          <div class="d-flex align-center justify-end">
            <span class="body-2 font-weight-black secondary--text mr-2">{{ item.capacity }}</span>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon x-small color="grey lighten-1" v-bind="attrs" v-on="on">
                  {{ item.production_unit_m2 ? 'fas fa-vector-square' : 'fas fa-layer-group' }}
                </v-icon>
              </template>
              <span>{{ item.production_unit_m2 ? 'Capacidad en m²' : 'Capacidad en Unidades' }}</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:item.hours_per_day="{ item }">
          <span class="body-2 font-weight-medium">{{ item.hours_per_day }} hrs</span>
        </template>

        <template v-slot:item.saturday="{ item }">
          <v-icon small :color="item.saturday ? 'success' : 'grey lighten-3'">
            {{ item.saturday ? 'fas fa-calendar-check' : 'fas fa-calendar' }}
          </v-icon>
        </template>

        <template v-slot:item.sunday="{ item }">
          <v-icon small :color="item.sunday ? 'success' : 'grey lighten-3'">
            {{ item.sunday ? 'fas fa-calendar-check' : 'fas fa-calendar' }}
          </v-icon>
        </template>

        <template v-slot:item.sort="{ item }">
          <v-chip x-small class="font-weight-black grey lighten-4" label>{{ item.sort }}</v-chip>
        </template>

        <template v-slot:item.enabled="{ item }">
          <v-switch
            v-model="item.enabled"
            color="success"
            hide-details
            dense
            @change="() => { machine = item; save() }"
          />
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center">
            <v-btn icon small color="primary" class="grey lighten-4 shadow-sm" @click.stop="edit(item)">
              <v-icon x-small>fas fa-edit</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialog -->
    <v-dialog v-model="showDialog" max-width="600" persistent>
      <v-card outlined class="modern-card overflow-hidden">
        <machine-create-update 
          :machine="machine"
          :loading="loading"
          @save="save"
          @close="showDialog = false"
        /> 
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { fetchMachines, createMachine, updateMachine } from '@/api/production'
import notifications from '@/utils/notifications'
import MachineCreateUpdate from '@/components/production/MachineCreateUpdate.vue'

export default {
  name: 'Machines',
  components: { MachineCreateUpdate },
  data() {
    return {
      loading: false,
      machines: [],
      machine: {},
      search: '',
      headers: [
        { text: 'Área y Equipamiento', align: 'start', value: 'name', class: 'grey lighten-4' },
        { text: 'Capacidad/Hr', value: 'capacity', class: 'grey lighten-4', align: 'end', width: '130' },
        { text: 'Jornada', value: 'hours_per_day', class: 'grey lighten-4', align: 'end', width: '100' },
        { text: 'Sab.', value: 'saturday', class: 'grey lighten-4', align: 'center', width: '80' },
        { text: 'Dom.', value: 'sunday', class: 'grey lighten-4', align: 'center', width: '80' },
        { text: 'Orden', value: 'sort', class: 'grey lighten-4', width: '80', align: 'center' },
        { text: 'Activo', value: 'enabled', class: 'grey lighten-4', width: '100', align: 'center', sortable: false },
        { text: 'Acciones', value: 'actions', class: 'grey lighten-4', width: '100', align: 'center', sortable: false }
      ],
      showDialog: false,
    }
  },
  async created() {
    this.fetch()
  },
  methods: {
    async fetch() {
      this.loading = true
      try {
        this.machines = await fetchMachines()
      } catch (err) {
        notifications.showError('Error cargando máquinas: ' + err)
      } finally {
        this.loading = false
      }
    },
    create() {
      this.machine = { id: -1, name: '', capacity: 1, production_unit_m2: false, enabled: true, saturday: false, sunday: false }
      this.showDialog = true
    },
    edit(item) {
      this.machine = { ...item }
      this.showDialog = true
    },
    async save() {
      this.loading = true
      try {
        if (this.machine.id > 0) {
          const res = await updateMachine(this.machine)
          notifications.showSuccess(`Área ${res.name} actualizada`)
        } else {
          const res = await createMachine(this.machine)
          notifications.showSuccess(`Área ${res.name} creada`)
        }
        this.showDialog = false
        await this.fetch()
      } catch (err) {
        notifications.showError('Error al guardar: ' + err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 12px !important;
}
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }
.gap-1 { gap: 4px; }
</style>